const API_BASE_URL = process.env.REACT_APP_API_URL;

const getAuthHeaders = (token: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

export interface SignedUploadUrl {
  uploadUrl: string;
  fileId: string;
  expiresAt: string;
}

/**
 * Request a signed upload URL from the backend.
 * The backend creates a GCS signed URL scoped to this user/file.
 */
export const getSignedUploadUrl = async (
  token: string,
  contentType: string,
  fileName: string
): Promise<SignedUploadUrl> => {
  const response = await fetch(`${API_BASE_URL}/api/uploads/signed-url`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ contentType, fileName }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to get upload URL: ${response.status}`);
  }

  return response.json();
};

/**
 * Upload a file directly to GCS using the signed URL.
 * Returns when the upload is complete.
 */
export const uploadToSignedUrl = (
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);

    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed due to a network error'));
    xhr.send(file);
  });
};

/**
 * Full signed-upload flow: get a signed URL, upload to GCS, return the fileId
 * so the caller can notify the backend to process it.
 */
export const uploadFileViaSignedUrl = async (
  token: string,
  file: Blob,
  fileName: string,
  contentType: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  const { uploadUrl, fileId } = await getSignedUploadUrl(token, contentType, fileName);
  await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
  return fileId;
};
